// What a project's pages share while it's open (the Mac's LibraryModel for one window): the project, the live
// documents to write through, the arrangement shown, and the few pieces of view state more than one page needs —
// the fragment the caret is in (the inspector follows it), the item being renamed, and a way back to the editor.
//
// Structure edits (`structureEdits`) are each one `live.edit` call: one change per document they touch, so one outbox
// batch per changed document for each thing the writer did.
import { createContext, useContext } from 'react';
import { journalPostsInTree, postContaining, type ArrangementInfo, type DocumentEditor, type MetadataValue, type Placement, type Project, type TreeNode } from '@writing-suite/core';
import { PlanEditor, type SavedSearch, type SavedSearchRule } from '@writing-suite/core/plan';
import { unfilteredPlacement, type BinderFilterState, type FilterView, type Snippet } from '../store/binderFilter.ts';
import type { LiveProject } from '../store/project.ts';
import { rememberNewPostCaret } from '../store/newPostCaret.ts';
import type { Writing } from '../store/writing.ts';
import {
  fragmentsOf, locate, newPlacement, placementParent, postsBecomeSections, sectionsBecomePosts, treeConverting, treeMoving, type StructureAsk,
} from '../views/structure.ts';

export interface Workspace {
  project: Project;
  live: LiveProject | null;
  arrangement: ArrangementInfo;
  /** The arrangement as the contents show it: with a filter on, only the matches and the folders above them. */
  tree: TreeNode[];
  /** The whole arrangement (the same as `tree` when nothing is filtered): where new items and moves really go. */
  fullTree?: TreeNode[];
  /** The contents' filter, which the pages follow. */
  filter?: BinderFilter | null;
  /** Shows the contents column (the phone's drawer), its filter field focused. */
  showColumn?: () => void;
  /** The page's node (the route's). */
  node: TreeNode | null;
  /** Text and fields can be written (the project is open live; the Trash reads only). */
  canWrite: boolean;
  /** Structure can be edited here (writable, and not the Trash or a collection). */
  canStructure: boolean;
  /** Comments and suggestions can be added (every role; a commenter's only writes, W9a). */
  canComment: boolean;
  /** This account's role in the project: its own (`owner`), or shared with it as a co-writer or commenter. */
  role: 'owner' | 'cowriter' | 'commenter';
  /** Whose project it is, when it's shared with this account. */
  ownerName: string | null;
  caretFragment: string | null;
  setCaretFragment(id: string | null): void;
  renaming: string | null;
  /** Where the rename was started: only that view shows a title field. Two fields for one item (the corkboard's card and
   *  the contents' row) took the focus from each other, and the blur ended the rename before it could be typed. */
  renamingIn: RenameSite | null;
  setRenaming(nodeId: string | null, where?: RenameSite): void;
  /** Puts the caret back in the editor (after naming a new scene, a menu, the format bar). */
  focusEditor(): void;
  setEditorFocus(fn: (() => void) | null): void;
  /** Opens a node on a page ("room/page"; the page shown if it's about an item, else Write). */
  open(nodeId: string | null, page?: string): void;
  /** Asks, then moves these nodes to the Trash. */
  askTrash(nodeIds: string[]): void;
  /** Shows a structure question (a journal's posts becoming sections, or sections posts) or a refusal (no `confirm`). */
  askStructure(ask: StructureAsk): void;
  /** Opens a passage in Write (the fragment's node, the range selected: UTF-16 in its text). */
  reveal(target: RevealTarget): void;
  /** A passage waiting for Write to select it (after the page opens). */
  revealing: RevealTarget | null;
  clearReveal(): void;
  /** The project-wide sheets: a checkpoint's name, Find in Project, a sprint. */
  openSheet(sheet: Sheet): void;
  /** This browser's session and sprint for the project. */
  writing: Writing | null;
  /** The fragments Write shows now ("Open text" for Find). */
  shownFragments: string[];
  setShownFragments(ids: string[]): void;
}

/** The contents' filter as the shell holds it (store/binderFilter.ts has the logic). */
export interface BinderFilter {
  state: BinderFilterState;
  /** The chips and the typed words, when they narrow anything. */
  active: SavedSearch | null;
  /** The arrangement filtered (null when nothing is). */
  view: FilterView | null;
  /** The collection the filter came from, and whether it's been changed since. */
  saved: SavedSearch | null;
  edited: boolean;
  set(next: BinderFilterState): void;
  addFacet(rule: SavedSearchRule): void;
  replaceFacet(rule: SavedSearchRule): void;
  removeFacet(id: string): void;
  filterBy(rules: SavedSearchRule[], adding?: boolean): void;
  /** A collection's facets as the filter (the manuscript shown, for a manuscript collection). */
  load(search: SavedSearch): void;
  focus(nodeId: string | null): void;
  clear(): void;
  /** Whether a fragment is among the matches (always, when nothing is filtered, and for one the arrangement shown
   *  doesn't hold). */
  matchesFragment(fragmentId: string): boolean;
  /** The manuscript's fragments the filter lets through, whichever arrangement shows, and how many documents of how
   *  many (Publish ▸ only the filtered scenes). */
  filteredManuscript: { fragments: string[]; documents: number; total: number } | null;
  /** A row drawn faintly: a folder shown for what's under it, or a document edited out of the filter. */
  isContext(nodeId: string): boolean;
  /** Text around the filter's words in a document, for the row under its title. */
  snippet(fragmentId: string): Snippet | null;
  /** Next or Previous Document, through the matches when filtering. */
  step(forward: boolean): void;
}

export interface RevealTarget {
  fragmentId: string;
  from?: number;
  to?: number;
}

/** The view whose title field a rename uses: the contents column (the default) or the corkboard's grid. */
export type RenameSite = 'contents' | 'board';

export type Sheet = 'checkpoint' | 'find' | 'sprint' | 'reimport';

export const WorkspaceContext = createContext<Workspace | null>(null);

/** The workspace through a stable reference, for views memoized against the project's rebuilds (the contents): they
 *  read the workspace as it is now in their handlers, and re-render only when their own props change. */
export const WorkspaceRefContext = createContext<{ current: Workspace | null }>({ current: null });

export function useWorkspace(): Workspace | null {
  return useContext(WorkspaceContext);
}

/** The structure edits, over the workspace's live project and arrangement. */
export function structureEdits(w: Pick<Workspace, 'project' | 'live' | 'arrangement' | 'tree' | 'fullTree'> & Partial<Pick<Workspace, 'askStructure'>>) {
  const live = w.live;
  const arrangementId = w.arrangement.id;
  // With a filter on, items go where they would unfiltered, and a move among the visible rows lands beside the visible
  // neighbour (hidden siblings keep their places).
  const all = w.fullTree ?? w.tree;
  const edit = <T>(fn: Parameters<LiveProject['edit']>[0]): T | null => (live ? (live.edit(fn) as T) : null);
  // A refusal (a numbered post into another, a document holding posts, a lock) is shown, not thrown at the view.
  const refusing = (fn: () => void) => {
    try { fn(); } catch (e) {
      if (!w.askStructure) throw e;
      w.askStructure({ title: e instanceof Error ? e.message : String(e), message: '' });
    }
  };
  // A journal's posts, where a move can make posts sections.
  const posts = w.project.type === 'journal' && w.arrangement.role === 'manuscript';
  const metadataOf = (id: string) => w.project.fragment(id)?.metadata;
  const api = {
    /** New scene (or post) / New folder, placed as the Mac places them; returns the new node. */
    newItem(selectedId: string | null, folder: boolean): string | null {
      const post = w.project.type === 'journal' && w.arrangement.role === 'manuscript' && !folder;
      const placement = newPlacement(all, selectedId, { folder, post });
      // A journal's new post starts as a draft holding the journal's opening and sign-off (`PlanEditor.newPost`, as New Post),
      // the caret waiting between them for Write.
      if (post) {
        const made = edit<ReturnType<PlanEditor['newPost']>>((e) =>
          new PlanEditor((e as unknown as DocumentEditor).docs, (e as unknown as DocumentEditor).context).newPost({ placement, title: 'Untitled' }));
        rememberNewPostCaret(w.project.id, made);
        return made?.nodeId ?? null;
      }
      const made = edit<{ nodeId: string }>((e) => e.newFragment({ arrangementId, placement, title: folder ? 'New Folder' : 'Untitled', isFolder: folder }));
      return made?.nodeId ?? null;
    },
    rename(nodeId: string, title: string) {
      const trimmed = title.trim();
      const node = locate(all, nodeId)?.node;
      if (!trimmed || !node?.fragmentId || trimmed === node.title) return;
      edit((e) => e.renameFragment(node.fragmentId!, trimmed));
    },
    move(nodeIds: string[], placement: Placement, confirmed = false) {
      if (!nodeIds.length) return;
      const real = all === w.tree ? placement : unfilteredPlacement(all, w.tree, nodeIds, placement);
      const question = posts && !confirmed && w.askStructure ? postsBecomeSections(all, treeMoving(all, nodeIds, placementParent(all, real)), metadataOf) : null;
      if (question) {
        w.askStructure!({ ...question, onConfirm: () => api.move(nodeIds, placement, true) });
        return;
      }
      refusing(() => edit((e) => e.move(nodeIds, arrangementId, real)));
    },
    setFolder(nodeIds: string[], isFolder: boolean, confirmed = false) {
      const question = posts && isFolder && !confirmed && w.askStructure ? sectionsBecomePosts(all, nodeIds) : null;
      if (question) {
        w.askStructure!({ ...question, confirm: 'Convert to Folder', onConfirm: () => api.setFolder(nodeIds, isFolder, true) });
        return;
      }
      refusing(() => edit((e) => e.setFolder(nodeIds, arrangementId, isFolder)));
    },
    /** Whether New Section can put one beside this item: a journal post or one of its sections. */
    canAddSection(selectedId: string | null): boolean {
      return posts && !!selectedId && !!postContaining(selectedId, journalPostsInTree(all));
    },
    /** New Section (`LibraryModel.newSection`): in the chosen post, after the chosen section, else last; returns its node. */
    newSection(selectedId: string): string | null {
      const post = postContaining(selectedId, journalPostsInTree(all));
      if (!posts || !post) return null;
      const node = locate(all, selectedId)?.node;
      const placement: Placement = selectedId === post.nodeId ? { inside: post.nodeId } : node?.isFolder ? { inside: selectedId } : { after: selectedId };
      return edit<{ nodeId: string }>((e) => e.newFragment({ arrangementId, placement, title: 'Untitled' }))?.nodeId ?? null;
    },
    /** Lock or Unlock: against accidental change (a folder's lock covers everything under it). */
    setLocked(nodeIds: string[], locked: boolean) {
      const ids = nodeIds.map((id) => locate(all, id)?.node.fragmentId).filter((id): id is string => !!id);
      edit((e) => { for (const id of ids) (e as unknown as DocumentEditor).setLocked(id, locked); });
    },
    /** Numbered or not: a chapter or part that takes no number (a prologue) is counted past by compile and the contents. */
    setNumbered(nodeIds: string[], numbered: boolean) {
      const ids = nodeIds.map((id) => locate(all, id)?.node.fragmentId).filter((id): id is string => !!id);
      edit((e) => { for (const id of ids) (e as unknown as DocumentEditor).setNumbered(id, numbered); });
    },
    trash(nodeIds: string[]) {
      edit((e) => e.trash(nodeIds, arrangementId));
    },
    /** One field on every selected item's fragment, in one change per fragment. */
    setField(nodeIds: string[], fieldId: string, value: MetadataValue | null) {
      const ids = fragmentsOf(all, nodeIds);
      if (ids.length) edit((e) => ids.flatMap((id) => e.setMetadata(id, fieldId, value)));
    },
  };
  return api;
}
